import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ColumnDef, DataTableState, DirtyCell } from './types';

interface UseDataTableStateConfig {
  storageKey: string;
  defaultSort?: string;
  defaultOrder?: 'asc' | 'desc';
  defaultLimit?: number;
  columns: ColumnDef<any>[];
  filterKeys?: string[];
}

function dirtyCellKey(rowId: string, columnId: string) {
  return `${rowId}::${columnId}`;
}

export function useDataTableState(config: UseDataTableStateConfig) {
  const {
    storageKey,
    defaultSort = '',
    defaultOrder = 'asc',
    defaultLimit = 25,
    columns,
    filterKeys = [],
  } = config;

  const [searchParams, setSearchParams] = useSearchParams();

  // Load column visibility and viewMode from localStorage
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(`dt-cols-${storageKey}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Validate saved IDs against current columns
        const validIds = new Set(columns.map((c) => c.id));
        const filtered = (parsed as string[]).filter((id) => validIds.has(id));
        // If new columns were added with defaultVisible, include them
        const newDefaults = columns
          .filter((c) => c.defaultVisible !== false && !parsed.includes(c.id))
          .map((c) => c.id);
        if (filtered.length > 0) return [...filtered, ...newDefaults];
      }
    } catch {}
    return columns
      .filter((c) => c.defaultVisible !== false)
      .map((c) => c.id);
  });

  const [viewMode, setViewMode] = useState<'table' | 'grid'>(() => {
    try {
      const saved = localStorage.getItem(`dt-view-${storageKey}`);
      if (saved === 'grid' || saved === 'table') return saved;
    } catch {}
    return 'table';
  });

  const [editMode, setEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editingColumns, setEditingColumns] = useState<Set<string>>(new Set());
  const [dirtyCells, setDirtyCells] = useState<Map<string, DirtyCell>>(new Map());

  // Derive URL-based state
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = parseInt(searchParams.get('limit') || String(defaultLimit), 10);
  const search = searchParams.get('q') || '';
  const sort = searchParams.get('sort') || defaultSort;
  const order = (searchParams.get('order') || defaultOrder) as 'asc' | 'desc';
  const filters: Record<string, string> = {};
  for (const key of filterKeys) {
    const val = searchParams.get(key);
    if (val) filters[key] = val;
  }

  const state: DataTableState = useMemo(
    () => ({
      page,
      limit,
      search,
      sort,
      order,
      filters,
      selectedIds,
      visibleColumns,
      viewMode,
      editMode,
      editingColumns,
      dirtyCells,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, limit, search, sort, order, JSON.stringify(filters), selectedIds, visibleColumns, viewMode, editMode, editingColumns, dirtyCells]
  );

  const updateState = useCallback(
    (partial: Partial<DataTableState>) => {
      const newParams = new URLSearchParams(searchParams);
      let resetPage = false;

      if ('page' in partial && partial.page !== undefined) {
        newParams.set('page', String(partial.page));
      }
      if ('limit' in partial && partial.limit !== undefined) {
        newParams.set('limit', String(partial.limit));
        resetPage = true;
      }
      if ('search' in partial && partial.search !== undefined) {
        if (partial.search) newParams.set('q', partial.search);
        else newParams.delete('q');
        resetPage = true;
      }
      if ('sort' in partial && partial.sort !== undefined) {
        if (partial.sort) newParams.set('sort', partial.sort);
        else newParams.delete('sort');
      }
      if ('order' in partial && partial.order !== undefined) {
        newParams.set('order', partial.order);
      }
      if ('filters' in partial && partial.filters !== undefined) {
        for (const key of filterKeys) {
          if (partial.filters[key]) newParams.set(key, partial.filters[key]);
          else newParams.delete(key);
        }
        resetPage = true;
      }
      if ('selectedIds' in partial && partial.selectedIds !== undefined) {
        setSelectedIds(partial.selectedIds);
      }
      if ('visibleColumns' in partial && partial.visibleColumns !== undefined) {
        setVisibleColumns(partial.visibleColumns);
        localStorage.setItem(`dt-cols-${storageKey}`, JSON.stringify(partial.visibleColumns));
      }
      if ('viewMode' in partial && partial.viewMode !== undefined) {
        setViewMode(partial.viewMode);
        localStorage.setItem(`dt-view-${storageKey}`, partial.viewMode);
      }
      if ('editMode' in partial && partial.editMode !== undefined) {
        setEditMode(partial.editMode);
      }
      if ('editingColumns' in partial && partial.editingColumns !== undefined) {
        setEditingColumns(partial.editingColumns);
      }
      if ('dirtyCells' in partial && partial.dirtyCells !== undefined) {
        setDirtyCells(partial.dirtyCells);
      }

      if (resetPage) {
        newParams.set('page', '1');
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, filterKeys, storageKey]
  );

  // Helper: mark a cell as dirty
  const markDirty = useCallback(
    (rowId: string, columnId: string, value: unknown, originalValue: unknown) => {
      setDirtyCells((prev) => {
        const next = new Map(prev);
        const key = dirtyCellKey(rowId, columnId);
        // If value reverted to original, remove from dirty
        if (value === originalValue || String(value) === String(originalValue)) {
          next.delete(key);
        } else {
          next.set(key, { rowId, columnId, value, originalValue });
        }
        return next;
      });
    },
    []
  );

  // Helper: discard all dirty cells
  const discardChanges = useCallback(() => {
    setDirtyCells(new Map());
    setEditingColumns(new Set());
  }, []);

  // Helper: toggle column editing
  const toggleColumnEditing = useCallback(
    (columnId: string) => {
      setEditingColumns((prev) => {
        const next = new Set(prev);
        if (next.has(columnId)) {
          next.delete(columnId);
          // Remove dirty cells for this column
          setDirtyCells((prevDirty) => {
            const nextDirty = new Map(prevDirty);
            for (const [key, cell] of nextDirty) {
              if (cell.columnId === columnId) nextDirty.delete(key);
            }
            return nextDirty;
          });
        } else {
          next.add(columnId);
        }
        return next;
      });
    },
    []
  );

  // Clear selection when data changes (page, search, filters)
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, limit, search, sort, order]);

  return { state, updateState, markDirty, discardChanges, toggleColumnEditing };
}
